import type { AppConfig, FeatureConfig } from "./types"

/** Built-in defaults: the first of the four configuration layers. */
export const DEFAULT_CONFIG: AppConfig = {
  version: 1,
  branding: {
    name: "Jumble",
    shortName: "Jumble",
    description: "A user-friendly Nostr client for exploring relay feeds.",
    logo: "/logo.svg",
    favicon: "/favicon.svg",
    titleTemplate: "{page} · {name}",
    footerText: "",
    links: [],
  },
  design: {
    colors: {
      primary: "#7c62c8",
      accent: "#4f9cf9",
    },
    theme: {
      default: "dark",
      allowed: ["light", "dark", "pure-black"],
    },
    radius: 12,
    density: "comfortable",
    fontScale: 1,
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  },
  features: {
    explore: true,
    relayReviews: true,
    notifications: true,
    bookmarks: true,
    search: true,
    composer: true,
    relaySets: true,
    trending: true,
    imageUploads: false,
  },
  relays: {
    defaultRelays: [
      "wss://nos.lol/",
      "wss://relay.primal.net/",
      "wss://offchain.pub/",
      "wss://relay.ditto.pub/",
    ],
    searchRelays: ["wss://search.nos.today/", "wss://search.nostrarchives.com/"],
    trendingRelays: ["wss://trending.relays.land/"],
    discoveryRelays: ["wss://nos.lol/", "wss://relay.primal.net/", "wss://offchain.pub/"],
    discoveryCollectionsUrl:
      "https://raw.githubusercontent.com/CodyTseng/awesome-nostr-relays/master/dist/collections.json",
    relayOptions: {
      connectTimeout: 7000,
      publishTimeout: 9000,
      maxReconnectAttempts: 6,
      reconnectBaseDelay: 1200,
      verifyEvents: true,
      eoseTimeout: 4500,
      relayInfoTimeout: 6000,
      infoRefreshMs: 6 * 60 * 60 * 1000,
    },
  },
  navigation: {
    items: [
      { id: "home", icon: "home", path: "/", visible: true },
      { id: "explore", icon: "compass", path: "/explore", visible: true },
      { id: "notifications", icon: "bell", path: "/notifications", visible: true },
      { id: "search", icon: "search", path: "/search", visible: true },
      { id: "profile", icon: "user", path: "/profile", visible: true },
      { id: "bookmarks", icon: "bookmark", path: "/bookmarks", visible: true },
      { id: "settings", icon: "settings", path: "/settings", visible: true },
    ],
  },
  i18n: {
    defaultLanguage: "en",
    languages: ["en", "zh"],
  },
  community: {
    mode: false,
    relaySets: [],
    extraRelays: [],
  },
}

export const FEATURE_KEYS: Array<keyof FeatureConfig> = [
  "explore",
  "relayReviews",
  "notifications",
  "bookmarks",
  "search",
  "composer",
  "relaySets",
  "trending",
  "imageUploads",
]

/** Clone that is safe for the nested plain data in the config. */
export function cloneConfig<T>(value: T): T {
  return structuredClone(value)
}
